/**
 * Notifications + the two persistent live regions (§11 accessibility): polite for routine state changes,
 * assertive for failures and conflicts. Toasts deduplicate by id — N refusals show ONE "wasn't saved".
 *
 * An error whose sentence came from a failed save or server call carries "Report" (callers change nothing: the
 * provider looks the sentence up). Such a notification waits while the pointer is over it or focus is inside it,
 * stays while its report is being sent, and leaves three seconds after it reads "Reported".
 *
 * A modal dialog paints above the page and makes everything outside itself inert. So the notifications and the live
 * regions sit in one element of their own, and while a modal dialog is open that element is inside the topmost one:
 * a notification raised over a dialog is seen, announced, and pressed there, and Tab stays within the dialog.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle2, AlertTriangle, Info, X } from 'lucide-react';
import { cn } from '@/lib/cn';
import { findFailure, type Failure } from '@/lib/failures';
import { canReport } from '@/lib/report';
import { ReportControl } from './ReportControl';

type Kind = 'success' | 'error' | 'warning' | 'info';
interface Toast { id: string; kind: Kind; message: string; action?: { label: string; run: () => void }; failure?: Failure }
interface Api {
  show: (t: Omit<Toast, 'id' | 'failure'> & { id?: string }) => void;
  success: (message: string, id?: string) => void;
  error: (message: string, id?: string, action?: Toast['action']) => void;
  warning: (message: string, id?: string) => void;
  announce: (message: string, urgent?: boolean) => void;
  dismiss: (id: string) => void;
}
const Ctx = createContext<Api | null>(null);
/** Opened with showModal. A browser too old to know `:modal` only ever opens this app's dialogs that way. */
const isModal = (d: HTMLDialogElement) => { try { return d.matches(':modal'); } catch { return true; } };

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [polite, setPolite] = useState('');
  const [assertive, setAssertive] = useState('');
  const timers = useRef(new Map<string, number>());
  // for notifications that carry Report: the element, and where its report stands
  const nodes = useRef(new Map<string, HTMLElement>());
  const reports = useRef(new Map<string, 'sending' | 'done'>());

  const dismiss = useCallback((id: string) => {
    setToasts((t) => t.filter((x) => x.id !== id));
    const h = timers.current.get(id);
    if (h) window.clearTimeout(h);
    timers.current.delete(id);
    reports.current.delete(id);
  }, []);

  /** (Re)starts the one timer a notification has. One whose explanation is open is asked again later, not closed. */
  const arm = useCallback((id: string, ms: number) => {
    const prev = timers.current.get(id);
    if (prev) window.clearTimeout(prev);
    timers.current.set(id, window.setTimeout(() => {
      if (nodes.current.get(id)?.querySelector('dialog[open]')) arm(id, ms);
      else dismiss(id);
    }, ms));
  }, [dismiss]);
  const hold = useCallback((id: string) => {
    if (reports.current.get(id) === 'done') return; // "Reported" leaves on its own clock
    const h = timers.current.get(id);
    if (h) window.clearTimeout(h);
    timers.current.delete(id);
  }, []);
  /** The pointer left or focus moved: once neither is on the notification (and no report is under way), the wait starts over. */
  const release = useCallback((id: string) => {
    window.setTimeout(() => {
      const el = nodes.current.get(id);
      if (!el || !el.isConnected || reports.current.has(id)) return;
      if (el.matches(':hover') || el.contains(document.activeElement) || el.querySelector('dialog[open]')) return;
      arm(id, 10000);
    }, 0);
  }, [arm]);

  const [layer] = useState(() => document.createElement('div'));
  useEffect(() => {
    layer.dataset.toastLayer = '';
    // the open modal dialogs, oldest first — the last one is on top
    const stack = [...document.querySelectorAll<HTMLDialogElement>('dialog[open]')];
    const place = (records: MutationRecord[] = []) => {
      for (const r of records) {
        if (r.type !== 'attributes' || !(r.target instanceof HTMLDialogElement)) continue;
        const at = stack.indexOf(r.target);
        if (at >= 0) stack.splice(at, 1);
        if (r.target.open) stack.push(r.target);
      }
      // a notification's own explanation is a dialog inside this element: never a home for it
      for (let i = stack.length - 1; i >= 0; i--) {
        const d = stack[i]!;
        if (!d.isConnected || !d.open || !isModal(d) || layer.contains(d)) stack.splice(i, 1);
      }
      const home = stack[stack.length - 1] ?? document.body;
      if (layer.parentNode === home) return;
      const focused = layer.contains(document.activeElement) ? (document.activeElement as HTMLElement) : null;
      // a notification already on screen does not rise in a second time
      layer.querySelectorAll<HTMLElement>('[data-toast-kind]').forEach((el) => { el.style.animation = 'none'; });
      home.appendChild(layer);
      // an explanation that was open in a notification left the top layer with the move
      layer.querySelectorAll<HTMLDialogElement>('dialog[open]').forEach((d) => d.close());
      focused?.focus();
      // focus that the move dropped sends no blur: a notification that was waiting on it starts its wait again
      nodes.current.forEach((_, id) => { if (!timers.current.has(id)) release(id); });
    };
    place();
    const seen = new MutationObserver(place);
    seen.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['open'] });
    return () => { seen.disconnect(); layer.remove(); };
  }, [layer, release]);

  const announce = useCallback((message: string, urgent = false) => {
    const set = urgent ? setAssertive : setPolite;
    set('');
    window.setTimeout(() => set(message), 30);
  }, []);

  const show = useCallback<Api['show']>(
    ({ id, kind, message, action }) => {
      const key = id ?? `${kind}:${message}`;
      // an error that a failed save or server call produced can be reported; a caller's own action keeps its place
      const found = kind === 'error' && !action ? findFailure(message) : null;
      const failure = found && canReport(found) ? found : undefined;
      reports.current.delete(key);
      setToasts((t) => [...t.filter((x) => x.id !== key), { id: key, kind, message, action, failure }].slice(-4));
      announce(message, kind === 'error' || kind === 'warning');
      // Errors stay long enough to read (~10 s); confirmations are brief.
      arm(key, kind === 'error' || kind === 'warning' ? 10000 : 4000);
    },
    [announce, arm],
  );

  const api = useMemo<Api>(
    () => ({
      show,
      success: (message, id) => show({ kind: 'success', message, id }),
      error: (message, id, action) => show({ kind: 'error', message, id, action }),
      warning: (message, id) => show({ kind: 'warning', message, id }),
      announce,
      dismiss,
    }),
    [show, announce, dismiss],
  );

  return (
    <Ctx.Provider value={api}>
      {children}
      {createPortal(<>
      <div className="sr-only" aria-live="polite" aria-atomic="true" data-testid="live-polite">{polite}</div>
      <div className="sr-only" aria-live="assertive" aria-atomic="true" data-testid="live-assertive">{assertive}</div>
      <div className="pointer-events-none fixed inset-x-0 bottom-0 z-toast flex flex-col items-center gap-2 whitespace-normal p-4 text-left font-normal normal-case tracking-normal sm:items-end" data-testid="toast-region">
        {toasts.map((t) => (
          <div key={t.id} role={t.kind === 'error' ? 'alert' : 'status'} data-toast-kind={t.kind}
            {...(t.failure ? {
              ref: (el: HTMLDivElement | null) => { if (el) nodes.current.set(t.id, el); else nodes.current.delete(t.id); },
              onPointerEnter: () => hold(t.id), onPointerLeave: () => release(t.id), onFocus: () => hold(t.id), onBlur: () => release(t.id),
            } : {})}
            className={cn('pointer-events-auto flex w-full max-w-md items-start gap-3 rounded-card bg-popover px-4 py-3 text-sm text-ink elevate animate-rise',
              t.kind === 'error' && 'ring-1 ring-st-danger/50')}>
            {t.kind === 'success' ? <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-st-success" aria-hidden /> : t.kind === 'info' ? <Info className="mt-0.5 size-4 shrink-0 text-st-info" aria-hidden /> : <AlertTriangle className={cn('mt-0.5 size-4 shrink-0', t.kind === 'error' ? 'text-st-danger' : 'text-st-warning')} aria-hidden />}
            {t.failure ? (
              <div className="flex min-w-0 flex-1 flex-col items-start gap-1">
                <p className="max-w-full">{t.message}</p>
                <ReportControl key={t.failure.at} surface="toast" failure={t.failure} side="top"
                  onStart={() => { reports.current.set(t.id, 'sending'); hold(t.id); }}
                  onSettled={(reported) => {
                    if (reports.current.get(t.id) !== 'sending') return; // dismissed or replaced while the report was on its way
                    if (reported) { reports.current.set(t.id, 'done'); arm(t.id, 3000); }
                    else { reports.current.delete(t.id); release(t.id); }
                  }} />
              </div>
            ) : <p className="min-w-0 flex-1">{t.message}</p>}
            {t.action && (
              <button type="button" className="shrink-0 font-semibold text-ink underline decoration-brand decoration-2 underline-offset-2" onClick={() => { t.action!.run(); dismiss(t.id); }}>
                {t.action.label}
              </button>
            )}
            <button type="button" aria-label="Dismiss notification" title="Dismiss notification" className="shrink-0 text-muted hover:text-ink" onClick={() => dismiss(t.id)}>
              <X className="size-4" aria-hidden />
            </button>
          </div>
        ))}
      </div>
      </>, layer)}
    </Ctx.Provider>
  );
}

export function useToast(): Api {
  const v = useContext(Ctx);
  if (!v) throw new Error('useToast outside ToastProvider');
  return v;
}
